import IconRender from 'components/common/IconRender'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CustomColorsRef, IconSizes } from 'types/general'
import { normalizeLangCode } from 'utils/i18nLocale'

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'pt', label: 'PT' },
]

export default function LanguageSwitcher() {
  const { i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const handleLanguageSelect = (langCode: string) => {
    i18n.changeLanguage(langCode)
    setIsOpen(false)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside)
      }, 100)
      
      return () => {
        clearTimeout(timeoutId)
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
    
    return undefined
  }, [isOpen])

  const activeLangCode = normalizeLangCode(i18n.language)
  const selectedLanguage =
    LANGUAGES.find(lang => lang.code === activeLangCode) ?? LANGUAGES[1]

  return (
    <div className="relative w-full text-sm max-w-fit" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 font-medium text-sm text-imovSecondary hover:text-imovSecondaryDark transition-colors cursor-pointer outline-none focus:outline-none"
      >
        <IconRender
          icon="regular-globe"
          size={IconSizes.SM}
          color={CustomColorsRef.Secondary}
        />
        <span>{selectedLanguage ? selectedLanguage.label : LANGUAGES[0].label}</span>
        <IconRender
          icon={isOpen ? 'regular-chevron-up' : 'regular-chevron-down'}
          size={IconSizes.SM}
          color={CustomColorsRef.Secondary}
        />
      </button>

      {isOpen && dropdownRef.current && (
        <div
          className="fixed z-50 bg-white border border-gray-200 rounded-md shadow-lg text-sm min-w-[4rem]"
          style={{
            top: dropdownRef.current.getBoundingClientRect().bottom + 4,
            left: dropdownRef.current.getBoundingClientRect().left,
          }}
        >
          <div className="max-h-[40vh] overflow-y-auto">
            <div className="space-y-1">
              {LANGUAGES.map(lang => {
                const isSelected = activeLangCode === lang.code

                return (
                  <div
                    key={lang.code}
                    className={`px-3 py-2 cursor-pointer hover:bg-blue-100 flex items-center justify-between ${
                      isSelected ? 'bg-imovPrimary bg-opacity-5 border-l-2 border-imovPrimary' : ''
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleLanguageSelect(lang.code)
                    }}
                  >
                    <span className={isSelected ? 'text-imovPrimary font-medium' : 'text-gray-700'}>
                      {lang.label}
                    </span>
                    {isSelected && (
                      <IconRender
                        icon="regular-check"
                        size={IconSizes.SM}
                        color={CustomColorsRef.Primary}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
